import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useMeasure, useMeasures } from '../../hooks/useMeasures';
import { usePipelineNodes } from '../../hooks/usePipelineNodes';
import { usePipelineNode } from '../../hooks/usePipelineNode';
import { useReportingTree, getAllConnectedNodeIds } from '../../hooks/useReportingTree';
import { useMeasureJoinPathOptions } from '../../hooks/useMeasureJoinPathOptions';
import { measureService } from '../../services/measure.service';
import { fetchFieldValues } from '../../services/pipelineNode.service';
import BackgroundService from '../../services/bg.service';
import type { Measure, MetricType, MeasureAggregation, DataWhitelist, DimensionJoinTree, MeasureJoinTreeNode } from '../../services/measure.service';
import { useDimensions } from '../../services/reportBuilder.service';
import { getAggregationOptionsForFieldType } from '../../services/metrics.utils';
import { filterValidParentMeasureIds } from '../../utils/measureUtils';
import { DimensionJoinPathsTable } from '../../components/DimensionJoinPathsTable';
import { JoinPathSelectionSheet } from '../../components/JoinPathSelectionSheet';
import { useQueryClient } from '@tanstack/react-query';
import { FilterTable, type FilterEntry } from '../../components/FilterTable';
import type { DataWhitelistEntry } from '../../components/UpstreamFilterTable';
import type { ComboboxOption } from '../../components/ui/combobox';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Combobox } from '../../components/ui/combobox';
import { MultiSelectCombobox } from '../../components/ui/multi-select-combobox';
import { Badge } from '../../components/ui/badge';
import { Loader2, Save, Hash, Calculator, Activity, ArrowLeft, Filter, Copy, Check, Network, AlertTriangle, RefreshCw, MoreVertical, Trash2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import { useTemplateName } from '../../hooks/useTemplates';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { cn } from '@/lib/utils';
import { getAvailableFormats } from '../../services/formatting.service';

const DISPLAY_FORMAT_OPTIONS = getAvailableFormats();

const COHORT_AGGREGATION_OPTIONS = [
  {
    value: 'COUNT_NON_ZERO_COHORT_CONDITION',
    label: 'Count Still-Active',
    description: 'Counts entities that had activity in both the prior period and this period, e.g. for retention.',
  },
  {
    value: 'COUNT_ZERO_COHORT_CONDITION',
    label: 'Count Deactivated',
    description: 'Counts entities that had activity in the prior period but not in this period, e.g. for churn.',
  },
  {
    value: 'COUNT_REACTIVATED_CONDITION',
    label: 'Count Reactivated',
    description: 'Counts entities that had activity before the prior period but not in the prior period, e.g. for reactivation.',
  },
  {
    value: 'COUNT_FIRST_ACTIVITY_CONDITION',
    label: 'Count New',
    description: 'Counts entities that had their first activity in this period, e.g. for acquisition.',
  },
  {
    value: 'SUM_COHORT_CONDITION',
    label: 'Sum',
    description: 'Sums up all activity for entities in this period, e.g. for LTV.',
  },
];

interface MetricTypeCardProps {
  type: MetricType;
  title: string;
  description: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
}

function MetricTypeCard({ type, title, description, icon, isSelected, onClick }: MetricTypeCardProps) {
  return (
    <Card
      className={cn(
        'cursor-pointer transition-colors hover:border-primary/50',
        isSelected && 'bg-primary/10 border-primary'
      )}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          {icon}
          <CardTitle className="text-base">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription>{description}</CardDescription>
      </CardContent>
    </Card>
  );
}

interface AvailableVariablesHelperProps {
  metrics: Measure[];
}

function AvailableVariablesHelper({ metrics }: AvailableVariablesHelperProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = async (columnName: string, metricId: string) => {
    try {
      await navigator.clipboard.writeText(columnName);
      setCopiedId(metricId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="space-y-2">
      <Label>Available Variables</Label>
      <p className="text-sm text-muted-foreground">
        Use these column names in your formula. Click to copy. Note: if the column names have special characters, you will have to wrap them in double quotes in your formula.
      </p>
      <div className="flex flex-wrap gap-2">
        {metrics.map((metric) => {
          const columnName = metric.column_name || metric.name;
          const isCopied = copiedId === metric.id;
          return (
            <button
              key={metric.id}
              type="button"
              onClick={() => handleCopy(columnName, metric.id)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-mono transition-colors',
                'bg-muted/50 hover:bg-muted border-border hover:border-primary/50',
                isCopied && 'bg-success/10 border-success text-success'
              )}
            >
              {isCopied ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
              )}
              {columnName}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function MetricDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: measure, isLoading, error } = useMeasure(id || '', { enabled: !!id });

  const isTemplateLocked = !!measure?.managed_by_template_id;
  const templateName = useTemplateName(measure?.managed_by_template_id || '');

  // Fetch all DIMENSION nodes (entities) for the entity selector
  const { data: nodesData, isLoading: nodesLoading } = usePipelineNodes({
    page: 1,
    pageSize: 1000,
    filters: { node_type: 'DIMENSION' },
  });

  // Fetch all measures for the calculated metric upstream selector
  const { data: allMeasuresData, isLoading: measuresLoading } = useMeasures({
    page: 1,
    pageSize: 1000,
  });

  // Fetch the reporting tree to get connected nodes for filtering
  const { data: reportingTree } = useReportingTree();

  // Fetch all nodes (for filter column options from connected nodes)
  const { data: allNodesData } = usePipelineNodes({
    page: 1,
    pageSize: 1000,
  });

  // Fetch all dimensions for cohort dimension selector
  const { data: dimensionsData, isLoading: dimensionsLoading } = useDimensions();

  // Fetch join path options for this measure (only for BASIC metrics)
  const { data: joinPathOptions, isLoading: joinPathOptionsLoading } = useMeasureJoinPathOptions(
    id || '',
    { enabled: !!id }
  );

  const [formData, setFormData] = useState<Partial<Measure>>({
    name: '',
    column_name: '',
    description: '',
    formatter: '',
    metric_type: 'BASIC',
    pipeline_node_id: '',
    field_id: '',
    aggregator: undefined,
    parent_measure_ids: [],
    formula: '',
    // Cohort fields
    activity_measure_id: '',
    cohort_dimension_id: '',
  });
  const [dataWhitelist, setDataWhitelist] = useState<DataWhitelist>({
    entries: [],
    logic_gate: 'AND',
  });
  const [dimensionJoinTrees, setDimensionJoinTrees] = useState<DimensionJoinTree[]>([]);
  const [activeDimensionPipelineNodeId, setActiveDimensionPipelineNodeId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [savedConnectedEntities, setSavedConnectedEntities] = useState<Set<string>>(new Set());

  // Copy dialog state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyName, setCopyName] = useState('');
  const [isCopying, setIsCopying] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Job status tracking for process_measures
  const [isProcessMeasuresRunning, setIsProcessMeasuresRunning] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const waitingForJobRef = useRef(false);

  // Fetch the selected node to get its fields
  const { data: selectedNode, isLoading: selectedNodeLoading } = usePipelineNode(
    formData.pipeline_node_id || null
  );

  // Build entity options for the combobox
  const entityOptions = useMemo(() => {
    if (!nodesData?.records) return [];
    return nodesData.records.map(node => ({
      value: node.id,
      label: node.label || node.name || node.id,
      description: node.description,
    }));
  }, [nodesData]);

  // Build upstream metric options for calculated metrics (exclude the current metric)
  const upstreamMetricOptions = useMemo(() => {
    if (!allMeasuresData?.records) return [];
    return allMeasuresData.records
      .filter(m => m.id !== id) // Exclude current metric
      .map(m => ({
        value: m.id,
        label: `${m.name}`,
        description: m.description,
      }));
  }, [allMeasuresData, id]);

  // Build activity metric options for cohort metrics (only BASIC metrics, exclude current)
  const activityMetricOptions = useMemo(() => {
    if (!allMeasuresData?.records) return [];
    return allMeasuresData.records
      .filter(m => m.id !== id && m.metric_type === 'BASIC')
      .map(m => ({
        value: m.id,
        label: m.name,
        description: m.description || `${m.aggregator || 'Aggregation'} metric`,
      }));
  }, [allMeasuresData, id]);

  // Build cohort dimension options
  const cohortDimensionOptions = useMemo(() => {
    if (!dimensionsData) return [];
    return dimensionsData.map(d => ({
      value: d.id,
      label: d.name,
      description: d.description || 'Dimension',
    }));
  }, [dimensionsData]);

  // Get the selected upstream metrics with their details
  const selectedUpstreamMetrics = useMemo(() => {
    if (!formData.parent_measure_ids?.length || !allMeasuresData?.records) return [];
    return formData.parent_measure_ids
      .map(measureId => allMeasuresData.records.find(m => m.id === measureId))
      .filter((m): m is Measure => m !== undefined);
  }, [formData.parent_measure_ids, allMeasuresData]);

  // Build column options from the selected node's fields (excluding FOREIGN_KEY types)
  const columnOptions = useMemo(() => {
    const options = [
      { value: '_PLB_UUID', label: 'Pliable Record ID', description: 'STRING' },
    ];
    if (!selectedNode?.fields) return options;
    return [
      ...options,
      ...selectedNode.fields
        .filter(field => field.type !== 'FOREIGN_KEY')
        .map(field => ({
          value: field.id || field.name || '',
          label: field.label || field.name || field.id || '',
          description: field.type,
        })),
    ];
  }, [selectedNode]);

  // Get the selected field to determine aggregation options
  const selectedField = useMemo(() => {
    if (!selectedNode?.fields || !formData.field_id) return null;
    return selectedNode.fields.find(f => f.id === formData.field_id || f.name === formData.field_id);
  }, [selectedNode, formData.field_id]);

  // Build aggregation options based on the selected field's type
  const aggregationOptions = useMemo(() => {
    if (!selectedField?.type) {
      // Return basic options if no field selected
      return getAggregationOptionsForFieldType('STRING');
    }
    return getAggregationOptionsForFieldType(selectedField.type);
  }, [selectedField]);

  // Create a lookup map for nodes by ID
  const nodesById = useMemo(() => {
    if (!allNodesData?.records) return new Map();
    return new Map(allNodesData.records.map(n => [n.id, n]));
  }, [allNodesData?.records]);

  // Get all connected node IDs from the reporting tree
  const connectedNodeIds = useMemo(() => {
    if (!formData.pipeline_node_id || !reportingTree?.edges) return [];
    return getAllConnectedNodeIds(formData.pipeline_node_id, reportingTree.edges);
  }, [formData.pipeline_node_id, reportingTree]);

  // Build column options for the filter table from all connected nodes' fields
  const filterColumnOptions: ComboboxOption[] = useMemo(() => {
    if (nodesById.size === 0 || connectedNodeIds.length === 0) return [];

    const options: ComboboxOption[] = [];
    for (const nodeId of connectedNodeIds) {
      const node = nodesById.get(nodeId);
      if (!node?.fields) continue;
      const nodeName = node.label || node.name || node.id;
      for (const field of node.fields) {
        if (field.type === 'FOREIGN_KEY') continue;
        const fieldName = field.label || field.name || field.id || '';
        options.push({
          value: `${node.id}:${field.id || field.name}`,
          label: `${nodeName}: ${fieldName}`,
          description: field.description || '(No description)',
        });
      }
    }
    return options;
  }, [nodesById, connectedNodeIds]);

  // Get whitelist node IDs for highlighting in join path UI
  const whitelistNodeIds = useMemo(() => {
    return dataWhitelist.entries.map(e => e.pipeline_node_id).filter(Boolean);
  }, [dataWhitelist]);

  // Compute current connected entities (metric entity + whitelist entities)
  const currentConnectedEntities = useMemo(() => {
    const entities = new Set<string>();
    if (formData.pipeline_node_id) {
      entities.add(formData.pipeline_node_id);
    }
    whitelistNodeIds.forEach(id => entities.add(id));
    return entities;
  }, [formData.pipeline_node_id, whitelistNodeIds]);

  // Check if connected entities have changed since last save
  const connectedEntitiesChanged = useMemo(() => {
    if (savedConnectedEntities.size === 0 && currentConnectedEntities.size === 0) return false;
    if (savedConnectedEntities.size !== currentConnectedEntities.size) return true;
    for (const entity of currentConnectedEntities) {
      if (!savedConnectedEntities.has(entity)) return true;
    }
    return false;
  }, [savedConnectedEntities, currentConnectedEntities]);

  // Get the active dimension join tree for the sheet
  const activeDimensionJoinTree = useMemo(() => {
    if (!activeDimensionPipelineNodeId) return undefined;
    return dimensionJoinTrees.find(djt => djt.pipeline_node_id === activeDimensionPipelineNodeId);
  }, [activeDimensionPipelineNodeId, dimensionJoinTrees]);

  // Sort join path options by path length
  const sortedJoinPathOptions = useMemo(() => {
    if (!joinPathOptions) return [];
    const getPathLength = (node: MeasureJoinTreeNode): number => {
      let len = 1;
      node.downstream?.forEach(d => { len += getPathLength(d); });
      return len;
    };
    return joinPathOptions.map(opt => ({
      ...opt,
      possible_paths: [...opt.possible_paths].sort((a, b) => getPathLength(a) - getPathLength(b))
    }));
  }, [joinPathOptions]);

  // Handle whitelist changes
  const handleWhitelistChange = useCallback((newWhitelist: DataWhitelist) => {
    setDataWhitelist(newWhitelist);
    setHasChanges(true);
  }, []);

  // Handle dimension enable
  const handleEnableDimension = useCallback((idx: number) => {
    setDimensionJoinTrees(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], ignore: false };
      return updated;
    });
    setHasChanges(true);
  }, []);

  // Handle dimension disable
  const handleDisableDimension = useCallback((idx: number) => {
    setDimensionJoinTrees(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], ignore: true };
      return updated;
    });
    setHasChanges(true);
  }, []);

  // Handle join path selection
  const handleSelectJoinPath = useCallback((pipelineNodeId: string, pathIndex: number) => {
    const pathOption = sortedJoinPathOptions.find(opt => opt.pipeline_node_id === pipelineNodeId);
    if (!pathOption) return;

    const selectedPath = pathOption.possible_paths[pathIndex];

    setDimensionJoinTrees(prev => {
      const existingIndex = prev.findIndex(djt => djt.pipeline_node_id === pipelineNodeId);
      if (existingIndex >= 0) {
        // Update existing entry
        return prev.map((djt, idx) =>
          idx === existingIndex ? { ...djt, join_tree: selectedPath } : djt
        );
      } else {
        // Create new entry
        return [...prev, { pipeline_node_id: pipelineNodeId, join_tree: selectedPath, ignore: false }];
      }
    });

    setActiveDimensionPipelineNodeId(null);
    setHasChanges(true);
  }, [sortedJoinPathOptions]);

  // Handle custom path save
  const handleCustomPathSave = useCallback((pipelineNodeId: string, customPath: MeasureJoinTreeNode) => {
    setDimensionJoinTrees(prev => {
      const existingIndex = prev.findIndex(djt => djt.pipeline_node_id === pipelineNodeId);
      if (existingIndex >= 0) {
        // Update existing entry
        return prev.map((djt, idx) =>
          idx === existingIndex ? { ...djt, join_tree: customPath, custom_path: customPath } : djt
        );
      } else {
        // Create new entry
        return [...prev, { pipeline_node_id: pipelineNodeId, join_tree: customPath, custom_path: customPath, ignore: false }];
      }
    });

    setActiveDimensionPipelineNodeId(null);
    setHasChanges(true);
  }, []);

  // Initialize form data when measure loads
  useEffect(() => {
    if (measure) {
      // Filter out any parent_measure_ids that reference deleted metrics
      const availableIds = (allMeasuresData?.records || []).map(m => m.id);
      const validParentMeasureIds = filterValidParentMeasureIds(
        measure.parent_measure_ids || [],
        availableIds,
      );

      setFormData({
        name: measure.name || '',
        column_name: measure.column_name || '',
        description: measure.description || '',
        formatter: measure.formatter || '',
        metric_type: measure.metric_type || 'BASIC',
        pipeline_node_id: measure.pipeline_node_id || '',
        field_id: measure.field_id || '',
        aggregator: measure.aggregator,
        parent_measure_ids: validParentMeasureIds,
        formula: measure.formula || '',
        // Cohort fields
        activity_measure_id: measure.activity_measure_id || '',
        cohort_dimension_id: measure.cohort_dimension_id || '',
      });
      setDataWhitelist(measure.data_whitelist || {
        entries: [],
        logic_gate: 'AND',
      });
      setDimensionJoinTrees(measure.dimension_join_trees || []);
      setHasChanges(false);

      // Track saved connected entities for change detection
      const savedEntities = new Set<string>();
      if (measure.pipeline_node_id) {
        savedEntities.add(measure.pipeline_node_id);
      }
      measure.data_whitelist?.entries?.forEach(entry => {
        if (entry.pipeline_node_id) {
          savedEntities.add(entry.pipeline_node_id);
        }
      });
      setSavedConnectedEntities(savedEntities);
    }
  }, [measure, allMeasuresData]);

  // Check if process_measures job is running when the component mounts (for BASIC metrics)
  useEffect(() => {
    if (!id || formData.metric_type !== 'BASIC' || !formData.pipeline_node_id) {
      return;
    }

    const checkJobStatus = async () => {
      try {
        const isRunning = await measureService.isProcessMeasuresRunning();
        setIsProcessMeasuresRunning(isRunning);

        // If running and not already waiting, start waiting for completion
        if (isRunning && !waitingForJobRef.current) {
          waitForJobCompletion();
        }
      } catch (err) {
        console.error('Error checking job status:', err);
      }
    };

    checkJobStatus();
  }, [id, formData.metric_type, formData.pipeline_node_id]);

  // Function to wait for job completion and refresh data
  const waitForJobCompletion = useCallback(async () => {
    const jobId = measureService.getProcessMeasuresJobId();
    if (!jobId || waitingForJobRef.current) {
      return;
    }

    waitingForJobRef.current = true;
    setIsProcessMeasuresRunning(true);

    try {
      await BackgroundService.getInstance().waitForJob(jobId, false, undefined, true);

      // Job completed - refresh the data
      queryClient.invalidateQueries({ queryKey: ['measure', id] });
      queryClient.invalidateQueries({ queryKey: ['measure-join-path-options', id] });

      toast.success('Dimension connections have been recalculated');
    } catch (err) {
      console.error('Error waiting for job:', err);
      // Don't show error toast for normal completion scenarios
    } finally {
      waitingForJobRef.current = false;
      setIsProcessMeasuresRunning(false);
    }
  }, [id, queryClient]);

  // Handler to manually trigger recalculation
  const handleRecalculate = useCallback(async () => {
    setIsRecalculating(true);
    try {
      await measureService.recalculateJoinPaths();
      toast.info('Recalculation started');

      // Start waiting for the job to complete
      waitForJobCompletion();
    } catch (err) {
      console.error('Error triggering recalculation:', err);
      toast.error('Failed to start recalculation');
      setIsRecalculating(false);
    }
  }, [waitForJobCompletion]);

  const handleInputChange = (field: keyof Measure, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleMetricTypeChange = (type: MetricType) => {
    setFormData(prev => ({ ...prev, metric_type: type }));
    setHasChanges(true);
  };

  const handleEntityChange = (nodeId: string) => {
    setFormData(prev => ({
      ...prev,
      pipeline_node_id: nodeId,
      field_id: '', // Reset field when entity changes
      aggregator: undefined, // Reset aggregator when entity changes
    }));
    setHasChanges(true);
  };

  const handleColumnChange = (fieldId: string) => {
    setFormData(prev => ({
      ...prev,
      field_id: fieldId,
      aggregator: undefined, // Reset aggregator when column changes
    }));
    setHasChanges(true);
  };

  const handleAggregatorChange = (aggregator: string) => {
    setFormData(prev => ({
      ...prev,
      aggregator: aggregator as MeasureAggregation,
    }));
    setHasChanges(true);
  };

  const handleParentMeasuresChange = (measureIds: string[]) => {
    setFormData(prev => ({ ...prev, parent_measure_ids: measureIds }));
    setHasChanges(true);
  };

  const handleFormulaChange = (formula: string) => {
    setFormData(prev => ({ ...prev, formula }));
    setHasChanges(true);
  };

  // Cohort handlers
  const handleCohortAggregatorChange = (aggregator: string) => {
    setFormData(prev => ({
      ...prev,
      aggregator: aggregator as MeasureAggregation,
    }));
    setHasChanges(true);
  };

  const handleActivityMetricChange = (measureId: string) => {
    setFormData(prev => ({
      ...prev,
      activity_measure_id: measureId,
    }));
    setHasChanges(true);
  };

  const handleCohortDimensionChange = (dimensionId: string) => {
    setFormData(prev => ({
      ...prev,
      cohort_dimension_id: dimensionId,
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!id) return;

    setIsSaving(true);
    try {
      await measureService.save({
        id,
        ...formData,
        // Send null explicitly when empty so backend clears it, not undefined which omits the field
        data_whitelist: dataWhitelist.entries.length > 0 ? dataWhitelist : null,
        dimension_join_trees: dimensionJoinTrees,
        // Cohort fields - only include if they have values
        activity_measure_id: formData.activity_measure_id || undefined,
        cohort_dimension_id: formData.cohort_dimension_id || undefined,
      });

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['measure', id] });
      queryClient.invalidateQueries({ queryKey: ['measures'] });

      // Update saved connected entities to match current state
      setSavedConnectedEntities(new Set(currentConnectedEntities));

      setHasChanges(false);
      toast.success('Metric saved successfully');

      // For BASIC metrics, saving triggers process_measures - wait for it to complete
      if (formData.metric_type === 'BASIC' && formData.pipeline_node_id) {
        // Small delay to allow the job to be queued
        setTimeout(() => {
          waitForJobCompletion();
        }, 500);
      } else {
        // For non-BASIC metrics, just invalidate join path options immediately
        queryClient.invalidateQueries({ queryKey: ['measure-join-path-options', id] });
      }
    } catch (err) {
      console.error('Failed to save metric:', err);
      toast.error('Failed to save metric');
    } finally {
      setIsSaving(false);
    }
  };

  useUnsavedChangesGuard(hasChanges, handleSave);

  // Convert a name to a SQL-safe column name (uppercase with underscores)
  const toSqlSafeColumnName = (name: string): string => {
    return name
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_') // Replace non-alphanumeric chars with underscores
      .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
  };

  // Handle copy metric
  const handleCopyMetric = async () => {
    if (!measure || !copyName.trim()) return;

    setIsCopying(true);
    try {
      // Create a copy with all the same parameters but a new name and column_name
      const { id: _id, created_at, updated_at, ...measureData } = measure;
      const trimmedName = copyName.trim();
      const newMeasure = await measureService.create({
        ...measureData,
        name: trimmedName,
        column_name: toSqlSafeColumnName(trimmedName),
      });

      // Invalidate the measures query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['measures'] });
      toast.success('Metric copied successfully');
      setCopyDialogOpen(false);
      setCopyName('');
      // Navigate to the new metric
      navigate(`/develop/metrics/${newMeasure.id}`);
    } catch (err) {
      console.error('Failed to copy metric:', err);
      toast.error('Failed to copy metric');
    } finally {
      setIsCopying(false);
    }
  };

  // Handle delete metric
  const handleDeleteMetric = async () => {
    if (!measure || deleteConfirmName !== measure.name) return;

    setIsDeleting(true);
    try {
      await measureService.delete(measure.id);
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['measures'] });
      toast.success('Metric deleted successfully');
      setDeleteDialogOpen(false);
      // Navigate back to the metrics list
      navigate('/develop/metrics');
    } catch (err) {
      console.error('Failed to delete metric:', err);
      toast.error('Failed to delete metric');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading metric...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !measure) {
    return (
      <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
        <div className="flex items-center justify-center h-full">
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              Failed to load metric. Please try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          {/* Breadcrumbs */}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/develop">Develop</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/develop/metrics">Metrics</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{measure.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          {/* Page Title */}
          <h1 className="text-xl font-semibold text-foreground">{measure.name}</h1>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/develop/metrics')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          {!isTemplateLocked && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setCopyName(`${measure.name} (Copy)`);
                setCopyDialogOpen(true);
              }}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Metric
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setDeleteDialogOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Metric
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          {!isTemplateLocked && (
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isTemplateLocked && (
          <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-4 mb-6 max-w-4xl">
            <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              This metric is managed by template{templateName ? ` "${templateName}"` : ''} and cannot be edited.
            </p>
          </div>
        )}
        <div className={cn("max-w-4xl space-y-8", isTemplateLocked && "pointer-events-none opacity-60")}>
          {/* Basic Information Form */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">General</h2>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Metric Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter metric name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="column_name">Column Header</Label>
                <Input
                  id="column_name"
                  value={formData.column_name}
                  onChange={(e) => handleInputChange('column_name', e.target.value)}
                  placeholder="Enter column header for reports"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter a description for this metric"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="formatter">Display Format</Label>
                <Select
                  value={formData.formatter || ''}
                  onValueChange={(value) => handleInputChange('formatter', value)}
                >
                  <SelectTrigger id="formatter">
                    <SelectValue placeholder="Select display format" />
                  </SelectTrigger>
                  <SelectContent>
                    {DISPLAY_FORMAT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Metric Type Selection */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Metric Type</h2>

            <div className="grid gap-4 md:grid-cols-3">
              <MetricTypeCard
                type="BASIC"
                title="Basic"
                description="A simple metric based on a single column with an aggregation like SUM, COUNT, or AVG."
                icon={<Hash className="h-5 w-5 text-muted-foreground" />}
                isSelected={formData.metric_type === 'BASIC'}
                onClick={() => handleMetricTypeChange('BASIC')}
              />

              <MetricTypeCard
                type="CALCULATED"
                title="Calculated"
                description="A metric defined by a formula combining other metrics, like Revenue / Orders."
                icon={<Calculator className="h-5 w-5 text-purple-500" />}
                isSelected={formData.metric_type === 'CALCULATED'}
                onClick={() => handleMetricTypeChange('CALCULATED')}
              />

              <MetricTypeCard
                type="COHORT"
                title="Cohort"
                description="A metric that tracks values for specific cohorts or segments over time."
                icon={<Activity className="h-5 w-5 text-blue-500" />}
                isSelected={formData.metric_type === 'COHORT'}
                onClick={() => handleMetricTypeChange('COHORT')}
              />
            </div>
          </div>

          {/* Metric Type Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {formData.metric_type === 'BASIC' && 'Basic Metric Configuration'}
                {formData.metric_type === 'CALCULATED' && 'Calculated Metric Configuration'}
                {formData.metric_type === 'COHORT' && 'Cohort Metric Configuration'}
              </CardTitle>
              <CardDescription>
                {formData.metric_type === 'BASIC' && 'Select the entity, column, and aggregation method for this metric.'}
                {formData.metric_type === 'CALCULATED' && 'Define the formula for this calculated metric.'}
                {formData.metric_type === 'COHORT' && 'Configure the cohort settings for this metric.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {formData.metric_type === 'BASIC' && (
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label>Entity</Label>
                    <Combobox
                      options={entityOptions}
                      value={formData.pipeline_node_id || ''}
                      onValueChange={handleEntityChange}
                      placeholder="Select an entity..."
                      emptyText="No entities found."
                      isLoading={nodesLoading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Column</Label>
                    <Combobox
                      options={columnOptions}
                      value={formData.field_id || ''}
                      onValueChange={handleColumnChange}
                      placeholder="Select a column..."
                      emptyText="No columns found."
                      disabled={!formData.pipeline_node_id}
                      isLoading={selectedNodeLoading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Aggregator</Label>
                    <Combobox
                      options={aggregationOptions}
                      value={formData.aggregator || ''}
                      onValueChange={handleAggregatorChange}
                      placeholder="Select an aggregator..."
                      emptyText="No aggregators available."
                      disabled={!formData.field_id}
                    />
                  </div>
                </div>
              )}

              {formData.metric_type === 'CALCULATED' && (
                <div className="grid gap-6">
                  {/* Upstream Metrics Selection */}
                  <div className="space-y-3">
                    <Label>Upstream Metrics</Label>
                    <p className="text-sm text-muted-foreground">
                      Select the metrics that will be used in your formula.
                    </p>
                    <MultiSelectCombobox
                      options={upstreamMetricOptions}
                      value={formData.parent_measure_ids || []}
                      onValueChange={handleParentMeasuresChange}
                      placeholder="Select metrics..."
                      emptyText="No metrics available."
                      isLoading={measuresLoading}
                    />
                  </div>

                  {/* Available Variables Helper */}
                  {selectedUpstreamMetrics.length > 0 && (
                    <AvailableVariablesHelper metrics={selectedUpstreamMetrics} />
                  )}

                  {/* Formula Input */}
                  <div className="space-y-2">
                    <Label htmlFor="formula">SQL Formula</Label>
                    <p className="text-sm text-muted-foreground">
                      Write a SQL expression using the column names above. Example: <code className="bg-muted px-1 rounded">DIV0(REVENUE, ORDERS)</code>
                    </p>
                    <Textarea
                      id="formula"
                      value={formData.formula || ''}
                      onChange={(e) => handleFormulaChange(e.target.value)}
                      placeholder="e.g., revenue / NULLIF(orders, 0)"
                      rows={4}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
              )}

              {formData.metric_type === 'COHORT' && (
                <div className="grid gap-6">
                  {/* Activity Metric */}
                  <div className="space-y-2">
                    <Label>Activity Metric</Label>
                    <p className="text-sm text-muted-foreground">
                      Select an existing metric to track as the activity (e.g., "Revenue", "Order Count").
                    </p>
                    <Combobox
                      options={activityMetricOptions}
                      value={formData.activity_measure_id || ''}
                      onValueChange={handleActivityMetricChange}
                      placeholder="Select a metric..."
                      emptyText="No basic metrics found. Create a basic metric first."
                      isLoading={measuresLoading}
                    />
                  </div>

                  {/* Cohort Dimension */}
                  <div className="space-y-2">
                    <Label>Cohort Dimension</Label>
                    <p className="text-sm text-muted-foreground">
                      Select the dimension that identifies cohort members (e.g., "Customer Email", "User ID").
                    </p>
                    <Combobox
                      options={cohortDimensionOptions}
                      value={formData.cohort_dimension_id || ''}
                      onValueChange={handleCohortDimensionChange}
                      placeholder="Select a dimension..."
                      emptyText="No dimensions found."
                      isLoading={dimensionsLoading}
                    />
                  </div>

                  {/* Cohort Aggregation */}
                  <div className="space-y-2">
                    <Label>Cohort Aggregation</Label>
                    <p className="text-sm text-muted-foreground">
                      How should cohort members be counted based on their activity?
                    </p>
                    <Combobox
                      options={COHORT_AGGREGATION_OPTIONS}
                      value={formData.aggregator || ''}
                      onValueChange={handleCohortAggregatorChange}
                      placeholder="Select an aggregation..."
                      emptyText="No aggregations available."
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Data Filters - Only shown for BASIC metrics */}
          {formData.metric_type === 'BASIC' && formData.pipeline_node_id && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-muted-foreground" />
                  <CardTitle className="text-base">Data Filters</CardTitle>
                </div>
                <CardDescription>
                  Filter the data used when calculating this metric. Only records matching these conditions will be included.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filterColumnOptions.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    {selectedNodeLoading ? 'Loading columns...' : 'Select an entity to configure filters.'}
                  </div>
                ) : (
                  <FilterTable
                    filters={dataWhitelist.entries.map(entry => ({
                      columnId: `${entry.pipeline_node_id}:${entry.field_id}`,
                      comparator: entry.comparator,
                      value: entry.value,
                    }))}
                    onChange={(newFilters) => {
                      const newEntries: DataWhitelistEntry[] = newFilters.map(f => {
                        const [pipelineNodeId, fieldId] = f.columnId.split(':');
                        return {
                          pipeline_node_id: pipelineNodeId || '',
                          field_id: fieldId || '',
                          comparator: f.comparator,
                          value: f.value,
                        };
                      });
                      handleWhitelistChange({
                        ...dataWhitelist,
                        entries: newEntries,
                      });
                    }}
                    columnOptions={filterColumnOptions}
                    columnLabel="Column"
                    showComparator={true}
                    valueMode="multiselect"
                    onFetchValueOptions={fetchFieldValues}
                    defaultComparator="IN"
                    defaultValue={[]}
                  />
                )}
              </CardContent>
            </Card>
          )}

          {/* Dimension Connections - Only shown for BASIC metrics */}
          {formData.metric_type === 'BASIC' && formData.pipeline_node_id && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Network className="h-5 w-5 text-muted-foreground" />
                    <CardTitle className="text-base">Dimension Connections</CardTitle>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRecalculate}
                    disabled={isRecalculating || isProcessMeasuresRunning}
                  >
                    <RefreshCw className={cn("h-4 w-4 mr-2", (isRecalculating || isProcessMeasuresRunning) && "animate-spin")} />
                    Recalculate
                  </Button>
                </div>
                <CardDescription>
                  Configure how this metric connects to each dimension-enabled entity. This controls the joins generated when breaking down this metric by dimensions.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isProcessMeasuresRunning ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Recalculating Dimension Connections</p>
                      <p className="text-sm text-muted-foreground max-w-md">
                        The system is recalculating how this metric connects to your dimensions.
                        This may take a while if you have a large data model.
                        The page will update automatically when complete.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {connectedEntitiesChanged && (
                      <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm">
                          Connected entities have changed. Save the metric to reload join path options.
                        </span>
                      </div>
                    )}
                    {joinPathOptionsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <DimensionJoinPathsTable
                        dimensionJoinTrees={dimensionJoinTrees}
                        joinPathOptions={sortedJoinPathOptions}
                        metricPipelineNodeId={formData.pipeline_node_id || ''}
                        whitelistNodeIds={whitelistNodeIds}
                        onEnableDimension={handleEnableDimension}
                        onDisableDimension={handleDisableDimension}
                        onSelectPath={(pipelineNodeId) => setActiveDimensionPipelineNodeId(pipelineNodeId)}
                      />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Join Path Selection Sheet */}
          <JoinPathSelectionSheet
            open={!!activeDimensionPipelineNodeId}
            onOpenChange={(open) => {
              if (!open) setActiveDimensionPipelineNodeId(null);
            }}
            activePipelineNodeId={activeDimensionPipelineNodeId}
            dimensionJoinTree={activeDimensionJoinTree}
            joinPathOptions={sortedJoinPathOptions}
            metricPipelineNodeId={formData.pipeline_node_id || ''}
            whitelistNodeIds={whitelistNodeIds}
            dataWhitelist={dataWhitelist}
            onSelectPath={handleSelectJoinPath}
            onCustomPathSave={handleCustomPathSave}
            isLoading={joinPathOptionsLoading}
          />
        </div>
      </div>

      {/* Copy Metric Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={(open) => {
        setCopyDialogOpen(open);
        if (!open) setCopyName('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy Metric</DialogTitle>
            <DialogDescription>
              Create a copy of <strong>{measure.name}</strong> with a new name.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="py-4">
            <Label htmlFor="copy-name" className="mb-2 block">New Metric Name</Label>
            <Input
              id="copy-name"
              placeholder="Enter name for the copy"
              value={copyName}
              onChange={(e) => setCopyName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && copyName.trim()) {
                  handleCopyMetric();
                }
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCopyMetric}
              disabled={isCopying || !copyName.trim()}
            >
              {isCopying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Copying...
                </>
              ) : (
                'Create Copy'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Metric Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) setDeleteConfirmName('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Metric
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                Are you sure you want to delete <strong>{measure.name}</strong>?
              </p>
              <p className="text-destructive font-medium">
                Warning: Deleting this metric may cause downstream issues for any calculated metrics or reports that depend on it.
              </p>
              <p>
                To confirm, type the metric name below:
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="py-4">
            <Input
              placeholder={measure.name}
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteMetric}
              disabled={isDeleting || deleteConfirmName !== measure.name}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Confirm Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
